/**
 * Delta tone colored by the SIGN of the change so the color always matches the number:
 * positive Δ = green, negative Δ = red, ~zero = muted. (Simpler/more readable than the
 * per-metric good/bad sense.) Reuses Flow Optimizer's DeltaPill class language.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { CompareMetric } from './compareMetrics';

export type DeltaTone = 'good' | 'bad' | 'neutral';

const EPS = 1e-9;

/** Colored by sign: positive → green ('good'), negative → red ('bad'), ~0 → 'neutral'. */
export function deltaTone(_metric: CompareMetric, deltaAbs: number): DeltaTone {
  if (Math.abs(deltaAbs) < EPS) return 'neutral';
  return deltaAbs > 0 ? 'good' : 'bad';
}

/** Pill classes (border + bg + text) — matches DeltaPill. */
export const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  good: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-600',
  bad: 'border-destructive/40 bg-destructive/10 text-destructive',
  neutral: 'border-border bg-muted/40 text-muted-foreground',
};

/** Text-only color for the signed Δ value cell. */
export const DELTA_TONE_TEXT: Record<DeltaTone, string> = {
  good: 'text-emerald-600',
  bad: 'text-destructive',
  neutral: 'text-muted-foreground',
};

/** Series stroke/fill for this metric, e.g. "hsl(var(--stat-ftd-value))". */
export function metricStroke(metric: CompareMetric): string {
  return `hsl(var(${metric.colorToken}))`;
}
